import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  structuredData?: object;
  robots?: string; // e.g., "noindex, nofollow" for legal pages
}

export function useSEO({ 
  title, 
  description, 
  keywords, 
  canonical, 
  ogImage = '/src/assets/hero-pool-3.jpeg',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  structuredData,
  robots 
}: SEOProps) {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Helper function to update or create meta tags
    const updateMetaTag = (selector: string, attribute: string, value: string, content: string) => {
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute, value);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    // Update basic meta tags
    updateMetaTag('meta[name="description"]', 'name', 'description', description);
    
    if (keywords) {
      updateMetaTag('meta[name="keywords"]', 'name', 'keywords', keywords);
    }

    // Update robots meta tag if specified
    if (robots) {
      updateMetaTag('meta[name="robots"]', 'name', 'robots', robots);
    } else {
      // Remove robots meta tag if not specified (allow indexing)
      const robotsTag = document.querySelector('meta[name="robots"]');
      if (robotsTag) {
        robotsTag.remove();
      }
    }

    // Update canonical link
    if (canonical) {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonical);
    }

    // Ensure viewport meta tag exists
    if (!document.querySelector('meta[name="viewport"]')) {
      const viewport = document.createElement('meta');
      viewport.setAttribute('name', 'viewport');
      viewport.setAttribute('content', 'width=device-width, initial-scale=1, shrink-to-fit=no');
      document.head.appendChild(viewport);
    }

    // Update Open Graph tags
    const ogTags = [
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: ogType },
      { property: 'og:site_name', content: 'BlueLux Pool Services' },
      { property: 'og:image', content: ogImage },
      { property: 'og:image:alt', content: `${title} - BlueLux Pool Services` },
    ];

    if (canonical) {
      ogTags.push({ property: 'og:url', content: canonical });
    }

    ogTags.forEach(({ property, content }) => {
      updateMetaTag(`meta[property="${property}"]`, 'property', property, content);
    });

    // Update Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: twitterCard },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImage },
      { name: 'twitter:image:alt', content: `${title} - BlueLux Pool Services` },
    ];

    twitterTags.forEach(({ name, content }) => {
      updateMetaTag(`meta[name="${name}"]`, 'name', name, content);
    });

    // Add structured data if provided
    if (structuredData) {
      let structuredDataScript = document.querySelector('script[type="application/ld+json"]');
      if (!structuredDataScript) {
        structuredDataScript = document.createElement('script');
        structuredDataScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(structuredDataScript);
      }
      structuredDataScript.textContent = JSON.stringify(structuredData);
    }

  }, [title, description, keywords, canonical, ogImage, ogType, twitterCard, structuredData, robots]);
}